import React from "react"
import { Mail, ArrowLeft } from "lucide-react"
import { Link } from "react-router-dom"
import SEO from "../components/SEO"

export default function TermsOfUse() {
    // const currentDate = new Date().toLocaleDateString('en-US', {
    //     year: 'numeric',
    //     month: 'long',
    //     day: 'numeric'
    // })

    return (
        <div className="min-h-screen bg-gray-50">
            <SEO title="Terms of Use" />
            {/* Header */}
            <div className="bg-white border-b border-gray-200">
                <div className="container mx-auto px-4 md:px-6 py-6">
                    <Link to="/" className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium">
                        <ArrowLeft className="w-4 h-4" />
                        Back to Home
                    </Link>
                </div>
            </div>

            {/* Content */}
            <div className="container mx-auto px-4 md:px-6 py-12">
                <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-sm p-8 md:p-12">
                    <div className="mb-8">
                        <h1 className="text-4xl font-bold text-gray-900 mb-2">Terms of Service</h1>
                        <p className="text-gray-600">Bousanm LLC</p>
                        <p className="text-sm text-gray-500 mt-2">Last updated: January 31, 2026</p>
                    </div>

                    <div className="prose prose-blue max-w-none">
                        {/* Section 1 */}
                        <section className="mb-8">
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Agreement to Terms</h2>
                            <p className="text-gray-700 leading-relaxed mb-3">
                                By accessing BousanmTechnology.com, you agree to be bound by these Terms of Service. Bousanm Technology is a multi-service firm providing software development, global logistics, and e-commerce infrastructure.
                            </p>
                        </section>

                        {/* Section 2 */}
                        <section className="mb-8">
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Description of Services</h2>
                            <p className="text-gray-700 leading-relaxed mb-4">
                                Bousanm Technology operates three primary divisions:
                            </p>
                            <ul className="list-disc pl-6 space-y-4 text-gray-700">
                                <li>
                                    <span className="font-bold">Software Development:</span> Provision of custom software, API integrations, and enterprise digital solutions.
                                </li>
                                <li>
                                    <span className="font-bold">Global Shipping & Logistics:</span> International freight forwarding, courier services, and supply chain management.
                                </li>
                                <li>
                                    <span className="font-bold">E-commerce Infrastructure:</span> Tools and platforms for online sales, procurement, and digital brand scaling.
                                </li>
                            </ul>
                        </section>

                        {/* Section 3 */}
                        <section className="mb-8">
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Corporate Spending Tools (Stripe Issuing)</h2>
                            <p className="text-gray-700 leading-relaxed mb-4 font-medium">
                                As part of our operational infrastructure, we provide corporate cards to our employees, contractors, and business partners.
                            </p>
                            <div className="bg-blue-50 border border-blue-100 rounded-xl p-6 space-y-4">
                                <div>
                                    <h4 className="font-bold text-blue-900 mb-1">Authorized Use</h4>
                                    <p className="text-blue-800 text-sm">Cards are strictly for business-related expenses (procurement of software, logistics equipment, travel for field operations, etc.).</p>
                                </div>
                                <div>
                                    <h4 className="font-bold text-blue-900 mb-1">Ownership</h4>
                                    <p className="text-blue-800 text-sm">All cards remain the property of Bousanm LLC and can be frozen or canceled at any time via our automated systems.</p>
                                </div>
                                <div>
                                    <h4 className="font-bold text-red-900 mb-1">Prohibited Activities</h4>
                                    <p className="text-red-800 text-sm">Users may not use cards for personal expenses, gambling, or illegal activities.</p>
                                </div>
                            </div>
                        </section>

                        {/* Section 4 */}
                        <section className="mb-8">
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Shipping & Logistics Terms</h2>
                            <ul className="space-y-4 text-gray-700">
                                <li className="flex gap-3">
                                    <span className="font-bold text-slate-400">Liability:</span>
                                    <span>Bousanm Technology acts as a logistics coordinator. We are not liable for delays caused by customs, weather, or third-party carriers.</span>
                                </li>
                                <li className="flex gap-3">
                                    <span className="font-bold text-slate-400">Compliance:</span>
                                    <span>All packages must comply with international shipping regulations. We reserve the right to inspect any cargo handled through our network.</span>
                                </li>
                            </ul>
                        </section>

                        {/* Section 5 */}
                        <section className="mb-8">
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Software Development & Intellectual Property</h2>
                            <ul className="space-y-4 text-gray-700">
                                <li className="flex gap-3">
                                    <span className="font-bold text-slate-400">Licensing:</span>
                                    <span>Software delivered by Bousanm is licensed to the client. Ownership of the base code remains with Bousanm LLC unless otherwise specified in a custom contract.</span>
                                </li>
                                <li className="flex gap-3">
                                    <span className="font-bold text-slate-400">API Usage:</span>
                                    <span>Users must not attempt to reverse engineer or breach our financial or logistics APIs.</span>
                                </li>
                            </ul>
                        </section>

                        {/* Section 6 */}
                        <section className="mb-8">
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">6. User Accounts & KYC</h2>
                            <p className="text-gray-700 leading-relaxed mb-4">
                                To access our integrated platform, users must undergo a verification process.
                            </p>
                            <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
                                <li>You agree to provide accurate identification.</li>
                                <li>Bousanm Technology uses third-party regulated partners to conduct identity verification for the issuance of financial tools.</li>
                            </ul>
                        </section>

                        {/* Section 7 */}
                        <section className="mb-8">
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Limitation of Liability</h2>
                            <p className="text-gray-700 leading-relaxed">
                                Bousanm LLC shall not be liable for any indirect, incidental, or consequential damages resulting from the use of our software, shipping delays, or card transaction failures.
                            </p>
                        </section>

                        {/* Section 8 */}
                        <section className="mb-8">
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Termination</h2>
                            <p className="text-gray-700 leading-relaxed">
                                We reserve the right to terminate access to our services, cancel shipments, or deactivate corporate cards for any breach of these terms or suspicious activity.
                            </p>
                        </section>

                        {/* Section 9 */}
                        <section className="mb-8">
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Governing Law</h2>
                            <p className="text-gray-700 leading-relaxed">
                                These terms are governed by the laws of the State of Wyoming, USA. Any disputes shall be resolved in the courts of said jurisdiction.
                            </p>
                        </section>

                        {/* Section 10 */}
                        <section>
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Contact Us</h2>
                            <p className="text-gray-700 leading-relaxed mb-4">
                                For legal inquiries:
                            </p>
                            <a href="mailto:support@bousanmtechnology.com" className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium text-lg">
                                <Mail className="w-5 h-5" />
                                support@bousanmtechnology.com
                            </a>
                        </section>
                    </div>
                </div>
            </div>
        </div>
    )
}
